import type { Event } from '@sentry/browser';

// Sentry events are fed into AI for analysis, which makes any attacker-controlled
// free text inside an event a prompt-injection vector: an attacker types
// instructions into a free-text form field, triggers a GraphQL error, and that
// text lands verbatim in `event.extra` (e.g. GQLVariables, extraContext). The
// injection never rides in on the stack trace — it rides in on the data we attach.
//
// Defence is an allowlist BY SHAPE, not a field-name blocklist and not
// pattern-matching for "looks malicious" (both get bypassed by the next field or
// the next payload). We keep strings (both object keys and values) that match
// the shape of a debugging identifier and redact everything that looks like prose.
//
// Residual risk, stated honestly: a length-bounded token drawn from the
// identifier charset can still carry a terse injection (e.g.
// `ignorePreviousInstructions...`). This strips the high-bandwidth channels
// (newlines, spaces, URLs, paths, query strings, long prose), not every
// conceivable one. The durable complement is keeping the AI that reads these
// events egress-free — sanitising input and denying exfiltration are
// independent layers.

// Max length for a string to be treated as a "safe token" (ID / enum / hash /
// address). UUID=36, 0x-address=42, sha256-hex=64 — 80 covers known identifier
// shapes with headroom. Anything longer is treated as free text.
const MAX_TOKEN_LENGTH = 80;

// Sentry normalises extras to depth 3 by default; 6 is a safe ceiling that also
// bounds work on pathological inputs.
const MAX_DEPTH = 6;

// Breadth cap per array/object. beforeSend runs per-error in the browser and may
// traverse operation.variables (bulk mutations can be large), so cap the work —
// this also bounds how much attacker text can flood the AI's context window.
const MAX_ENTRIES = 100;

// A value is kept verbatim only if it matches the shape of a debugging
// identifier: the charset is alnum plus the punctuation that appears in UUIDs,
// enums, hashes, addresses, ISO timestamps and operation names (`_`, `-`, `.`,
// `:`). This intentionally excludes whitespace AND URL/path/query characters
// (`/ ? & = # @ + ...`), so a short URL like `https://evil.example/c2` is
// redacted. Trade-off: base64 IDs containing `/`, `+` or `=` are also redacted
// (base64url `-_` IDs still pass) — losing a debug ID is preferable to keeping
// a URL channel.
const SAFE_TOKEN = /^[A-Za-z0-9_:.-]*$/;

const redactString = (value: string): string =>
  value.length <= MAX_TOKEN_LENGTH && SAFE_TOKEN.test(value)
    ? value
    : `[redacted: free-text len=${value.length}]`;

const sanitizeValue = (value: unknown, depth: number): unknown => {
  // Strings are always evaluated regardless of depth — they are the payload.
  if (typeof value === 'string') {
    return redactString(value);
  }
  // Numbers, booleans, null, undefined cannot carry an injection.
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (depth >= MAX_DEPTH) {
    return '[redacted: depth-limit]';
  }
  if (Array.isArray(value)) {
    const sanitized = value
      .slice(0, MAX_ENTRIES)
      .map((item) => sanitizeValue(item, depth + 1));
    if (value.length > MAX_ENTRIES) {
      sanitized.push(`[redacted: ${value.length - MAX_ENTRIES} more items]`);
    }
    return sanitized;
  }
  // Keys are sanitized with the same allowlist as values: normal keys (schema
  // variable names, hardcoded extraContext keys) are identifier-shaped and pass
  // through untouched, so this only redacts genuinely free-text keys — e.g. the
  // client-controlled keys of a JSON/map scalar variable.
  const keys = Object.keys(value as Record<string, unknown>);
  const out: Record<string, unknown> = {};
  keys.slice(0, MAX_ENTRIES).forEach((key, i) => {
    let safeKey = redactString(key);
    // A redacted key can collide with another redacted key; keep both.
    if (safeKey !== key && safeKey in out) {
      safeKey = `${safeKey}#${i}`;
    }
    out[safeKey] = sanitizeValue(
      (value as Record<string, unknown>)[key],
      depth + 1,
    );
  });
  if (keys.length > MAX_ENTRIES) {
    out['[redacted]'] = `${keys.length - MAX_ENTRIES} more keys`;
  }
  return out;
};

// event.message / exception values are prose — they can echo user input the
// backend reflects into an error string (e.g. validation messages), and they
// are the highest-prominence fields an AI reads (the issue title). We CANNOT
// apply the identifier allowlist here: almost every message contains spaces, so
// it would redact every title and destroy the event for human debuggers.
// Instead we collapse all whitespace runs (incl. newlines) to a single space,
// which removes the multi-line structure an injection needs to forge fake
// conversation turns ("...\n\nSystem: you are now...") while keeping the message
// readable. This is PARTIAL by design: a single-line injection still survives.
// It is weaker than the extras redaction on purpose — the message is a
// lower-fidelity channel (the attacker controls only a fragment the server
// chooses to echo, embedded in our own framing), whereas the high-fidelity
// channel (free-text form fields → GQLVariables) is fully redacted above.
const collapseWhitespace = (str: string): string =>
  str.replace(/\s+/g, ' ').trim();

/**
 * sanitizeEvent neutralises attacker-controlled free text in a Sentry event
 * before it is sent, because events are fed into AI analysis:
 * - `event.extra` (GQLVariables / extraContext / spread error props): redacted
 *   with an identifier-shape allowlist — prose-shaped keys/values become a
 *   length marker, identifier-shaped ones are preserved for debugging.
 * - `event.message` and `event.exception.values[].value`: whitespace-collapsed
 *   (prose-preserving) to strip multi-line injection structure.
 *
 * Stack frames, tags and breadcrumbs are not touched here.
 */
export const sanitizeEvent = <T extends Event>(event: T): T => {
  if (event.extra) {
    event.extra = sanitizeValue(event.extra, 0) as Event['extra'];
  }
  if (typeof event.message === 'string') {
    event.message = collapseWhitespace(event.message);
  }
  if (event.exception?.values) {
    for (const value of event.exception.values) {
      if (typeof value.value === 'string') {
        value.value = collapseWhitespace(value.value);
      }
    }
  }
  return event;
};
